<script setup>
import { ref, computed } from 'vue';
import api from '@shop/frontend/web/js/api';
import useCartStore from '@vueApp/store/modules/cart.js';
import { useCartDataProvider } from '@vueApp/hooks/useCartDataProvider.js';

const cartStore = useCartStore();
const { isGuest, isFastBuy } = useCartDataProvider();

const props = defineProps({
  url: {
    type: String,
    default() {
      return '';
    },
  },
  options: {
    type: Object,
    default() {
      return {};
    },
  },
  smallBtn: {
    type: Boolean,
    default: false,
  },
  ticketsCount: {
    type: Number,
    default: 0,
  },
  firstEventId: {
    type: Number,
    default: 0,
  },
  eventType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['setAcquiringError']);

const isAcquiringPushkinOrCinema = ref(
  props.options.payType === 'Pushkin' || props.options.payType === 'Cinema',
);

const formatEventType = computed(() => (props.eventType === 'service_simple' ? 'service' : 'event'));

const acquiringButtonClass = computed(() => ({
  'js-get-fast-registration': isGuest,
  'i-acquirings-button_small': props.smallBtn,
  'i-acquirings-button_disabled': props.ticketsCount > 1 && isAcquiringPushkinOrCinema.value,
}));

function changePayAcquiring(acquiringId) {
  api.pay
    .changePayAcquiring({ orderId: props.options.orderId, acquiringId })
    .then((res) => {
      if (isGuest) {
        return;
      }

      return (window.location.href = props.url);
    })
    .catch((err) => {
      emit('setAcquiringError', {
        isAcquiringError: true,
        errorMessage: err.message ?? '',
      });
    });
}

function payForOrder(event) {
  const acquiringId = props.options.id;

  if (acquiringId !== 0) {
    event.preventDefault();
    changePayAcquiring(acquiringId);
    return;
  }

  if (isGuest) {
    event.preventDefault();
    cartStore.setCurrentOpenedEventId(props.firstEventId);
    cartStore.setFastBuyModalStatus(true);
    cartStore.setEventTypeToPay(formatEventType.value);
  }
}
</script>

<template>
  <button
    v-if="isGuest"
    class="btn i-acquirings-button js-get-modal"
    data-target="#loginModal"
  >
    <slot>Перейти к оплате</slot>
  </button>
  <div v-else class="i-acquirings-button__link-wrapper">
    <a
      :href="url"
      class="btn i-acquirings-button"
      :class="acquiringButtonClass"
      @click="payForOrder($event)"
    >
      <slot>
        <template v-if="!options.name"> Перейти к оплате </template>
        <template v-else> {{ options.name }}</template>
      </slot>
    </a>
  </div>
</template>

<style scoped lang="scss">
:deep(.popper) {
  width: 280px;
  padding: 5px;

  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;

  color: $black;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}

.i-acquirings-button {
  font-weight: 700;
  padding: 7.5px 26px;

  &__icon {
    width: 20px;
    height: 20px;
    margin-right: 5px;
    margin-top: -3px;
  }

  &_disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__popper {
    position: absolute;
    top: 0;
    right: 7px;
  }

  &__pushkin-tooltip {
    width: 12px;
    height: 12px;

    transition: color 0.3s ease;
  }

  &__link-wrapper {
    position: relative;
    color: #fff;
  }

  &:hover {
    + .i-acquirings-button__popper {
      color: $primary;
    }
  }

  @media (min-width: 1024px) {
    &_small {
      font-size: 0px;
      min-width: 100px;
    }
  }

  @media (max-width: $screen-xs) {
    width: 100%;
  }
}
</style>
